import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center font-medium rounded-full transition-colors',
  {
    variants: {
      variant: {
        default: 'bg-primary-100 text-primary-700 ring-1 ring-inset ring-primary-600/20',
        secondary: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-500/20',
        outline: 'border border-slate-300 text-slate-700 bg-transparent',
        success: 'bg-success-50 text-success-700 ring-1 ring-inset ring-success-500/20',
        warning: 'bg-warning-50 text-warning-600 ring-1 ring-inset ring-warning-500/20',
        destructive: 'bg-danger-50 text-danger-600 ring-1 ring-inset ring-danger-500/20',
        // Legacy aliases
        danger: 'bg-danger-50 text-danger-600 ring-1 ring-inset ring-danger-500/20',
        info: 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-600/20',
        neutral: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-500/20',
        primary: 'bg-primary-500 text-white',
      },
      size: {
        sm: 'px-1.5 py-0.5 text-[10px]',
        default: 'px-2 py-0.5 text-xs',
        md: 'px-2 py-0.5 text-xs',
        lg: 'px-2.5 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean
}

export function Badge({ className, variant, size, dot = false, children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    >
      {dot && (
        <span
          className={cn(
            'mr-1 w-1.5 h-1.5 rounded-full flex-shrink-0',
            variant === 'success' && 'bg-success-500',
            variant === 'warning' && 'bg-warning-500',
            (variant === 'destructive' || variant === 'danger') && 'bg-danger-500',
            (variant === 'info' || variant === 'default') && 'bg-primary-500',
            (variant === 'neutral' || variant === 'secondary') && 'bg-slate-400',
            variant === 'primary' && 'bg-white',
          )}
        />
      )}
      {children}
    </span>
  )
}

export { badgeVariants }
